import React, { FC } from 'react'
import { CertifiedProduct, SustainableProduct } from '@components/icons'
import { useTranslation } from 'next-i18next/pages'
import useIsMobile from '@lib/hooks/useIsMobile'
import s from './DesignProductAttributes.module.scss'

export type DesignProductAttribute = {
    id: string
    title: string
    description: string
    icon: string
}

type Props = {
    disenado_para_ti: string[]
}

const designAttributes: DesignProductAttribute[] = [
    {
        id: 'SOST',
        title: 'La opción sostenible',
        description:
            'Productos que cumplen con estándares de responsabilidad social y ambiental',
        icon: 'leaf'
    },
    {
        id: 'FSC',
        title: 'CERTIFICACIÓN FSC',
        description:
            'Asegura que la madera, papel o cartón utilizados en la elaboración del producto provienen de bosques manejados responsablemente.',
        icon: 'ribbon'
    },
    {
        id: 'GRS',
        title: 'CERTIFICACIÓN GRS',
        description:
            'Certifica que el producto fue fabricado con al menos un 20% de materiales reciclados.',
        icon: 'ribbon'
    },
    {
        id: 'OEKO',
        title: 'CERTIFICACIÓN OEKO-TEX STANDARD 100',
        description:
            'Certifica que todos los componentes del producto, como telas e hilos, están libres de sustancias químicas nocivas para la salud.',
        icon: 'ribbon'
    },
    {
        id: 'RDS',
        title: 'CERTIFICACIÓN RDS',
        description:
            'Certifica que el producto contiene plumas naturales, obtenidas en procesos que garantizan principios y criterios de bienestar animal.',
        icon: 'ribbon'
    },
    {
        id: 'BPA',
        title: 'SELLO LIBRE DE BPA',
        description:
            'Garantiza que el producto no contiene esta sustancia nociva para la salud.',
        icon: 'ribbon'
    },
    {
        id: 'TENCEL',
        title: 'CERTIFICACIÓN TENCEL',
        description:
            'Certifica que el producto contiene fibra a base de madera obtenida de la gestión sostenible de bosques a través de procesos productivos con bajo impacto ambiental.',
        icon: 'ribbon'
    }
]

const getGridTemplateColumns = (childCount: number): string => {
    let gridTemplateColumns: string

    switch (childCount) {
        case 1:
            gridTemplateColumns = '1fr'
            break
        case 2:
            gridTemplateColumns = '1fr 1fr'
            break
        case 3:
            gridTemplateColumns = '1fr 1fr 1fr'
            break
        case 4:
        case 5:
        case 6:
            gridTemplateColumns = '1fr 1fr'
            break
        case 7:
        default:
            gridTemplateColumns = '1fr'
    }

    return gridTemplateColumns
}

export const DesignProductAttributes: FC<Props> = ({ disenado_para_ti }) => {
    const isMobile = useIsMobile()
    const { t } = useTranslation(['pdp'])

    const attrFactory = (
        productAttributes: string[],
        availableAttributes: DesignProductAttribute[]
    ) => {
        const attr = productAttributes.map((d: string) => {
            const foundAttr = availableAttributes.find(
                (da: DesignProductAttribute) => da.id === d
            )
            if (foundAttr) {
                return (
                    <div
                        key={foundAttr.id}
                        className={s['design-attribute-container']}
                    >
                        <div className={s['design-attribute-icon']}>
                            {foundAttr.icon === 'leaf' ? (
                                <SustainableProduct height={52} />
                            ) : (
                                <CertifiedProduct height={52} />
                            )}
                        </div>
                        <div className={s['design-attribute-content']}>
                            <h3 className={s['design-attribute-title']}>
                                {foundAttr.title}
                            </h3>
                            <p className={s['design-attribute-description']}>
                                {foundAttr.description}
                            </p>
                        </div>
                    </div>
                )
            }
        })

        return attr
    }

    const attributeItems = attrFactory(disenado_para_ti, designAttributes)

    const gridTemplateColumns: string = getGridTemplateColumns(
        attributeItems.length
    )

    const attrGridGenerator = (attributeItems: JSX.Element[]) => {
        let firstRowItems: JSX.Element[] = []
        let secondRowItems: JSX.Element[] = []

        if (isMobile) {
            return (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        justifyItems: 'center'
                    }}
                >
                    {attributeItems}
                </div>
            )
        }

        const itemsLength = attributeItems.length
        const rowSlicing = itemsLength === 5 ? 2 : 1

        if (itemsLength === 5 || itemsLength === 7) {
            firstRowItems = attributeItems.slice(0, rowSlicing)
            secondRowItems = attributeItems.slice(rowSlicing)
        } else {
            firstRowItems = attributeItems
        }

        return (
            <div>
                <div
                    className={s['attribute-grid-desktop']}
                    style={{
                        gridTemplateColumns: gridTemplateColumns,
                        justifyItems: 'center'
                    }}
                >
                    {firstRowItems}
                </div>
                {secondRowItems.length ? (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            justifyItems: 'center'
                        }}
                    >
                        {secondRowItems}
                    </div>
                ) : null}
            </div>
        )
    }

    const attributeGrid = attrGridGenerator(attributeItems as JSX.Element[])

    return (
        <div className={s['attribute-grid-container']}>
            <h4 className={s['attributes-title-heading']}>
                {t('pdp||Designed for you')}
            </h4>
            {attributeGrid}
        </div>
    )
}
